import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { BaseForm, createPasswordField, createConfirmPasswordField } from './BaseForm';
import { KairosBrand } from './KairosBrand';
import './Auth.css';

interface ResetPasswordFormProps {
  onBackToLogin: () => void;
  showHeader?: boolean;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onBackToLogin, showHeader = true }) => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      // Verify the token and get the email
      verifyToken(tokenFromUrl);
    }
  }, [searchParams]);

  const verifyToken = async (token: string) => {
    try {
      await apiService.verifyResetToken(token);
    } catch (error) {
      setError('Invalid or expired reset token');
    }
  };

  const handleSubmit = async (formData: Record<string, string>) => {
    try {
      await apiService.resetPassword(token, formData.newPassword);
      setMessage('Your password has been reset successfully. You can now sign in with your new password.');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      throw new Error(errorMessage);
    }
  };

  const fields = [
    createPasswordField('newPassword', 'New Password', true, 'Enter your new password'),
    createConfirmPasswordField('newPassword', 'confirmPassword', 'Confirm New Password', true, 'Confirm your new password')
  ];

  const formContent = (
    <div className="auth-form-container">
      <BaseForm
        title="Reset Password"
        subtitle="Enter your new password below"
        fields={fields}
        onSubmit={handleSubmit}
        submitButtonText="Reset Password"
        showHeader={false}
        error={error}
        success={message}
        className="reset-password-form"
      >
        <div className="text-center mt-4">
          <button 
            onClick={onBackToLogin}
            className="text-sm text-primary-600 hover:text-primary-500 cursor-pointer transition-all duration-300 bg-transparent border-none p-2 rounded-md hover:bg-primary-50"
          >
            Back to Login
          </button>
        </div>
      </BaseForm>
    </div>
  );

  if (showHeader) {
    return (
      <div className="auth-container">
        <div className="auth-header">
          <KairosBrand 
            showLogo={true}
            logoClassName="h-72 w-auto"
            kairosClassName="cursor-pointer hover:opacity-80 transition-opacity"
          />
        </div>
        {formContent}
      </div>
    );
  }

  return formContent;
};